import { Component } from '@theme/component';

/**
 * Viewport width at which the tooltip switches from tap-to-open to hover, matching the
 * breakpoint the trigger's own styling uses.
 */
const DESKTOP_BREAKPOINT = 1080;

/** Kept in sync with the panel's width in the snippet's stylesheet. */
const PANEL_WIDTH = 275;

/** Clearance kept between the panel and the viewport edges. */
const VIEWPORT_MARGIN = 10;

/** Gap between the trigger and the panel below it. */
const TRIGGER_OFFSET = 8;

/**
 * Grace period before a hover-out closes the panel, so crossing the gap between the
 * trigger and the panel doesn't dismiss it.
 */
const HOVER_CLOSE_DELAY = 150;

/**
 * An info icon that reveals a small panel of explanatory copy: hover on desktop, tap on
 * touch layouts, where a close button is offered too.
 *
 * The panel is a manual popover, so it renders in the top layer and escapes any scrolling
 * or clipping ancestor — it can sit inside a scrolling drawer or a modal dialog without
 * being cut off. Manual rather than auto because tapping an open tooltip's trigger has to
 * close it, and light dismiss would beat the tap to it and reopen instead; the same
 * dismissal is covered by the document listener at the bottom of this file. It also spares
 * every instance a unique id, since nothing needs `popovertarget`.
 *
 * Markup and styling live in snippets/info-tooltip.liquid, and every handler here is bound
 * declaratively from there — so none of this needs rebinding when a section re-renders or
 * a morph reorders the element.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The info icon.
 * @property {HTMLElement} panel - The popover holding the copy.
 *
 * @extends {Component<Refs>}
 */
class InfoTooltip extends Component {
  requiredRefs = ['trigger', 'panel'];

  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimer = null;

  /** Whether the layout is wide enough for hover to be the interaction. */
  get #isDesktop() {
    return window.innerWidth >= DESKTOP_BREAKPOINT;
  }

  get isOpen() {
    return this.refs.panel.matches(':popover-open');
  }

  /** Opens on hover, desktop only — a touch tap also fires pointerenter. */
  handlePointerEnter = () => {
    if (!this.#isDesktop) return;

    this.#cancelClose();
    this.#open();
  };

  /** Closes on hover-out, unless the pointer has moved onto the panel itself. */
  handlePointerLeave = () => {
    if (!this.#isDesktop) return;

    this.#cancelClose();
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;

      const { trigger, panel } = this.refs;
      if (!trigger.matches(':hover') && !panel.matches(':hover')) this.close();
    }, HOVER_CLOSE_DELAY);
  };

  /** Keeps the panel open while the pointer is over it. */
  handlePanelEnter = () => {
    if (this.#isDesktop) this.#cancelClose();
  };

  /**
   * Toggles the panel on tap. Desktop is hover-driven, so a click there is ignored —
   * otherwise clicking an already-hovered trigger would close it on the way in.
   * @param {Event} event - The click event from the trigger.
   */
  handleTriggerClick = (event) => {
    if (this.#isDesktop) return;

    // The trigger can sit inside a row that acts on clicks of its own, which shouldn't
    // also fire for a tap on the icon.
    event.preventDefault();
    event.stopPropagation();

    if (this.isOpen) this.close();
    else this.#open();
  };

  /**
   * Keeps clicks inside the panel from reaching whatever the tooltip is embedded in. The
   * panel renders in the top layer but is still a DOM descendant, so without this a tap on
   * the copy would act on the row around it — selecting a store, following a card's link.
   * @param {Event} event - The click event from the panel.
   */
  handlePanelClick = (event) => {
    event.stopPropagation();
  };

  /**
   * Dismisses the panel from its close button.
   * @param {Event} event - The click event from the close button.
   */
  handleCloseClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.close();
  };

  /**
   * Closes on Escape, which a manual popover doesn't do for us.
   * @param {Event} event - The keydown event from the trigger.
   */
  handleKeyDown = (event) => {
    if (event instanceof KeyboardEvent && event.key === 'Escape' && this.isOpen) this.close();
  };

  #open() {
    const { trigger, panel } = this.refs;

    // Showing an already-open popover throws rather than no-ops.
    if (!this.isOpen) panel.showPopover();

    this.#position();
    panel.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');

    // A fixed panel doesn't travel with its trigger, so it's repositioned as the page or
    // any scrolling ancestor moves, and dismissed once the trigger has scrolled away.
    // Capture, because scroll doesn't bubble from the element that actually scrolled.
    window.addEventListener('scroll', this.#reposition, { capture: true, passive: true });
    window.addEventListener('resize', this.#reposition, { passive: true });
  }

  close() {
    const { trigger, panel } = this.refs;

    this.#cancelClose();

    if (this.isOpen) panel.hidePopover();
    panel.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('aria-expanded', 'false');

    window.removeEventListener('scroll', this.#reposition, { capture: true });
    window.removeEventListener('resize', this.#reposition);
  }

  #cancelClose() {
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  }

  #reposition = () => {
    const bounds = this.refs.trigger.getBoundingClientRect();

    // Scrolled out of view: there's nothing left for the panel to point at.
    if (bounds.bottom < 0 || bounds.top > window.innerHeight) this.close();
    else this.#position();
  };

  /**
   * Pins the panel below its trigger and horizontally centred on it, pulled back inside
   * the viewport when that would overflow either edge. The panel is in the top layer, so
   * it positions against the viewport rather than any offset parent.
   */
  #position() {
    const { trigger, panel } = this.refs;
    const bounds = trigger.getBoundingClientRect();

    const centred = bounds.left + bounds.width / 2 - PANEL_WIDTH / 2;
    const rightmost = window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN;
    const left = Math.min(Math.max(centred, VIEWPORT_MARGIN), Math.max(rightmost, VIEWPORT_MARGIN));

    panel.style.left = `${left}px`;
    panel.style.top = `${bounds.bottom + TRIGGER_OFFSET}px`;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Reordering a list disconnects and reconnects its rows, which would otherwise leave
    // an open panel in the top layer with its scroll listeners still attached.
    this.close();
  }
}

if (!customElements.get('info-tooltip')) {
  customElements.define('info-tooltip', InfoTooltip);

  // Taps elsewhere dismiss an open tooltip. One listener for the whole page rather than
  // one per instance: a product grid or a store list can hold dozens of these.
  document.addEventListener('click', (event) => {
    const target = event.target;

    for (const panel of document.querySelectorAll('.info-tooltip__panel:popover-open')) {
      const tooltip = panel.closest('info-tooltip');

      if (
        tooltip instanceof InfoTooltip &&
        (!(target instanceof Node) || !tooltip.contains(target))
      ) {
        tooltip.close();
      }
    }
  });
}
